<script lang="ts">
  /// 担当者 複数選択 (ADR 20260528 / ACC-4)。 商談・契約は複数担当 (M:N) のため
  /// 単一の AccountSelect ではなくチップ + 追加ドロップダウンで複数選択する。
  /// 候補は invited + active。 選択中 id が inactive / 一覧外でも「(無効)」付きで
  /// チップ表示を維持し、 永続済み id が黙って消えないようにする。
  /// 主担当 (リード) の区別は持たない (フラット)。
  import { onMount } from 'svelte';
  import { listAssignableAccounts } from '@shared/api/accounts';
  import type { AssignableAccount } from '@shared/types/account';

  interface Props {
    /// 選択中の account id 配列。
    value: string[];
    /// 候補となる accounts。 未指定なら自前で listAssignableAccounts() する (テーブル等で
    /// 親が一括ロードして渡す場合は指定して N+1 fetch を避ける)。
    accounts?: AssignableAccount[] | null;
    disabled?: boolean;
    /// 選択変更時に呼ばれる任意コールバック (inline 保存用)。 新しい配列を渡す。
    onChange?: (value: string[]) => void;
  }

  let {
    value = $bindable([]),
    accounts = null,
    disabled = false,
    onChange,
  }: Props = $props();

  let loaded = $state<AssignableAccount[]>([]);
  let ready = $state(false);
  let error = $state<string | null>(null);
  // 追加ドロップダウンの選択値 (選択直後に '' に戻す)。
  let pending = $state('');

  // 親から accounts が渡されたらそれを使い、 無ければ自前ロード分を使う。
  let pool = $derived(accounts ?? loaded);

  let candidates = $derived(
    pool.filter((a) => a.status === 'invited' || a.status === 'active'),
  );
  // まだ選択されていない候補のみを追加ドロップダウンに出す。
  let addable = $derived(candidates.filter((a) => !value.includes(a.id)));

  // 選択中 id を Account に解決 (候補外でも pool にあれば detached 表示)。
  let selected = $derived(
    value.map((id) => ({
      id,
      account: pool.find((a) => a.id === id) ?? null,
      detached: !candidates.some((a) => a.id === id),
    })),
  );

  onMount(async () => {
    if (accounts) {
      ready = true;
      return;
    }
    try {
      loaded = await listAssignableAccounts();
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      ready = true;
    }
  });

  function label(a: AssignableAccount): string {
    return a.display_name;
  }

  function add() {
    if (!pending || value.includes(pending)) {
      pending = '';
      return;
    }
    value = [...value, pending];
    pending = '';
    onChange?.(value);
  }

  function remove(id: string) {
    value = value.filter((v) => v !== id);
    onChange?.(value);
  }
</script>

<div class="multi">
  {#if selected.length > 0}
    <ul class="chips">
      {#each selected as item (item.id)}
        <li class="chip" class:detached={item.detached}>
          <span class="chip-label">
            {item.account ? label(item.account) : item.id}{item.detached ? ' (無効)' : ''}
          </span>
          {#if !disabled}
            <button
              type="button"
              class="chip-remove"
              aria-label="担当者を外す"
              onclick={() => remove(item.id)}
            >×</button>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}

  {#if !disabled}
    <select bind:value={pending} onchange={add} {disabled}>
      <option value="">担当者を追加…</option>
      {#each addable as a (a.id)}
        <option value={a.id}>{label(a)}</option>
      {/each}
    </select>
  {/if}

  {#if !ready}
    <small class="hint">読込中…</small>
  {/if}
  {#if error}
    <small class="error">担当者の取得に失敗しました: {error}</small>
  {/if}
  {#if ready && selected.length === 0 && disabled}
    <span class="muted">—</span>
  {/if}
</div>

<style>
  .multi {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #eef2ff;
    color: #3730a3;
    padding: 2px 4px 2px 8px;
    border-radius: 999px;
    font-size: 0.75rem;
  }
  .chip.detached {
    background: #f3f4f6;
    color: #6b7280;
  }
  .chip-remove {
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font-size: 0.85rem;
    line-height: 1;
    padding: 0 2px;
  }
  select {
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: #fff;
    align-self: flex-start;
  }
  .hint {
    color: #6b7280;
    font-size: 0.7rem;
  }
  .error {
    color: #b91c1c;
    font-size: 0.7rem;
  }
  .muted {
    color: #6b7280;
    font-size: 0.875rem;
  }
</style>
